// src/components/BusinessImpactAnalysis.tsx
// Dynamic Business Impact Analysis component - updates as readiness sliders change

import React, { useMemo } from 'react';
import { BusinessIntelligenceData } from '../../../services/businessIntelligenceService';
import { ReadinessZone } from '../types';

interface PersonaContext {
  persona?: string;
  personaDisplayName?: string;
  assessmentId?: string | number;
  companyName?: string;
  industry?: string;
}

interface BusinessImpactAnalysisProps {
  businessData: BusinessIntelligenceData | null;
  currentScores: { [key: string]: number };
  readinessZones: ReadinessZone[];
  isSimulating: boolean;
  personaContext?: PersonaContext;
}

const BusinessImpactAnalysis: React.FC<BusinessImpactAnalysisProps> = ({ 
  businessData,
  currentScores,
  readinessZones,
  isSimulating,
  personaContext
}) => {
  // Calculate dynamic metrics based on current readiness levels
  const dynamicMetrics = useMemo(() => {
    if (!businessData) {
      return {
        revenueAtRisk: '$0',
        revenueAtRiskUnmeasured: false,
        revenuePercentage: '0.0',
        revenuePercentageUnmeasured: false,
        recoveryDays: 20, // Default to 20 days to match backend default
        frictionCost: 0,
        frictionUnmeasured: false
      };
    }

    // Task 8 makes the whole `actualRevenue` object null when revenue was never
    // measured — never a zeroed stand-in. Dividing by a substituted 0 here rendered
    // "Infinity% of annual revenue"; track the unmeasured state instead of computing.
    const revenueUnmeasured = businessData.actualRevenue === null || businessData.actualRevenue === undefined;
    const baseRevenue = businessData.actualRevenue?.amount || 0;
    // Same defect class as actualRevenue above: `revenueAtRiskRaw` does not exist on this
    // (legacy) BusinessIntelligenceData shape — the real figure is the nested
    // revenueAtRisk.amount, or revenueAtRisk === null when the session's revenue-at-risk
    // was never measured. The `|| 0` chain fabricated a confident $0 exposure for those
    // sessions instead of admitting the figure was never computed.
    const revenueAtRiskUnmeasured = businessData.revenueAtRisk === null || businessData.revenueAtRisk === undefined;
    const baseRevenueAtRisk = revenueAtRiskUnmeasured
      ? 0
      : (((businessData as { revenueAtRiskRaw?: number }).revenueAtRiskRaw) ?? businessData.revenueAtRisk.amount);
    const baseRecoveryDays = businessData.revenueAtRisk?.calculation?.recoveryDays ||
                             businessData.recoveryTimeline?.days || 20;

    let dynamicRevenueAtRisk: number;
    let dynamicRecoveryDays: number;

    if (isSimulating) {
      // During simulation: adjust based on readiness delta from baseline
      const readinessValues = Object.values(currentScores);
      const averageReadiness = readinessValues.reduce((sum, score) => sum + score, 0) / readinessValues.length;
      const apiRiskScore = businessData.revenueAtRisk?.calculation?.averageRiskScore || 0;
      const baselineReadiness = apiRiskScore > 0 ? (100 - apiRiskScore) : averageReadiness;
      const readinessImprovement = (averageReadiness - baselineReadiness) / 100;
      const riskReductionFactor = 1 - (readinessImprovement * 0.5);
      dynamicRevenueAtRisk = baseRevenueAtRisk * Math.max(0.3, Math.min(1.5, riskReductionFactor));
      const readinessImpact = (100 - averageReadiness) / 100;
      dynamicRecoveryDays = Math.round(baseRecoveryDays * (0.5 + readinessImpact));
    } else {
      // Not simulating: show the API values directly (consistent with top banner)
      dynamicRevenueAtRisk = baseRevenueAtRisk;
      dynamicRecoveryDays = baseRecoveryDays;
    }
    
    // Get friction cost from readiness zones (this is already dynamic)
    const totalFrictionCost = readinessZones.reduce((total, zone) => total + (zone?.frictionAmount || 0), 0);
    // All zones share one company's revenue measurement (see DefenseModule's
    // calculateReadinessZones) — a $0 total here would otherwise read as "no
    // friction to fix" for a company whose revenue we could not measure.
    const totalFrictionUnmeasured = readinessZones.some(zone => zone?.frictionUnmeasured === true);

    return {
      revenueAtRisk: dynamicRevenueAtRisk > 1000000 ?
        `$${(dynamicRevenueAtRisk / 1000000).toFixed(1)}M` :
        `$${Math.round(dynamicRevenueAtRisk / 1000)}K`,
      revenueAtRiskUnmeasured,
      // The percentage is revenue-at-risk OVER annual revenue — unmeasured if either half is.
      revenuePercentage: (revenueUnmeasured || revenueAtRiskUnmeasured) ? '0.0' : ((dynamicRevenueAtRisk / baseRevenue) * 100).toFixed(1),
      revenuePercentageUnmeasured: revenueUnmeasured || revenueAtRiskUnmeasured,
      recoveryDays: Math.max(1, dynamicRecoveryDays),
      frictionCost: totalFrictionCost,
      frictionUnmeasured: totalFrictionUnmeasured
    };
  }, [businessData, currentScores, readinessZones]);

  return (
    <div style={{
      padding: '20px',
      backgroundColor: 'var(--ra-glass-bg)',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      height: 'fit-content',
      position: 'relative'
    }}>
      {/* Dynamic indicator */}
      {isSimulating && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          backgroundColor: 'var(--ra-success)',
          color: 'white',
          fontSize: '10px',
          padding: '4px 8px',
          borderRadius: '12px',
          fontWeight: 'bold'
        }}>
          DYNAMIC
        </div>
      )}

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '20px',
        gap: '10px'
      }}>
        <span style={{ fontSize: '20px' }}>📊</span>
        <h3 style={{ 
          margin: 0,
          color: 'var(--ra-text-primary)',
          fontSize: '16px',
          fontWeight: 'bold'
        }}>
          Business Impact Analysis
          {personaContext?.personaDisplayName && (
            <div style={{
              fontSize: '12px',
              color: 'var(--ra-text-secondary)',
              fontWeight: 'normal',
              marginTop: '2px'
            }}>
              4-Persona Assessment • {personaContext.personaDisplayName} Perspective
            </div>
          )}
        </h3>
      </div>

      {/* Revenue at Risk Card */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '15px',
        color: 'white',
        position: 'relative',
        transition: 'all 0.3s ease'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '10px'
        }}>
          <span style={{ fontSize: '16px' }}>💰</span>
          <span style={{ fontSize: '14px', opacity: 0.9 }}>Revenue at Risk</span>
        </div>
        
        <div style={{
          fontSize: '32px',
          fontWeight: 'bold',
          marginBottom: '5px'
        }}>
          {dynamicMetrics.revenueAtRiskUnmeasured ? 'Not measured' : dynamicMetrics.revenueAtRisk}
        </div>

        <div style={{
          fontSize: '12px',
          opacity: 0.8
        }}>
          {dynamicMetrics.revenuePercentageUnmeasured ? 'Not measured' : `${dynamicMetrics.revenuePercentage}% of annual revenue`}
        </div>
      </div>

      {/* Recovery Timeline Card */}
      <div style={{
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '15px',
        color: 'white',
        position: 'relative',
        transition: 'all 0.3s ease'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '10px'
        }}>
          <span style={{ fontSize: '16px' }}>⏱️</span>
          <span style={{ fontSize: '14px', opacity: 0.9 }}>Recovery Timeline</span>
        </div>
        
        <div style={{
          fontSize: '32px',
          fontWeight: 'bold',
          marginBottom: '5px'
        }}>
          {dynamicMetrics.recoveryDays} days
        </div>
        
        <div style={{
          fontSize: '12px',
          opacity: 0.8
        }}>
          Full business recovery
        </div>
      </div>

      {/* Business Friction Cost Card */}
      <div style={{
        background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        borderRadius: '12px',
        padding: '20px',
        color: 'white',
        position: 'relative',
        transition: 'all 0.3s ease'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '10px'
        }}>
          <span style={{ fontSize: '16px' }}>⚡</span>
          <span style={{ fontSize: '14px', opacity: 0.9 }}>Business Friction Cost</span>
        </div>
        
        <div style={{
          fontSize: dynamicMetrics.frictionUnmeasured ? '20px' : '32px',
          fontWeight: 'bold',
          marginBottom: '5px'
        }}>
          {dynamicMetrics.frictionUnmeasured ? 'Not measured' : `$${Math.round(dynamicMetrics.frictionCost / 1000)}K`}
        </div>
        
        <div style={{
          fontSize: '12px',
          opacity: 0.8
        }}>
          Annual operational friction
        </div>
      </div>

      {/* Multi-Persona Enhancement Indicator */}
      {personaContext?.assessmentId && (
        <div style={{
          marginTop: '15px',
          padding: '8px',
          backgroundColor: 'rgba(0, 102, 255, 0.08)',
          borderRadius: '6px',
          fontSize: '11px',
          color: 'var(--ra-cyber-aqua)',
          textAlign: 'center',
          border: '1px solid rgba(0, 102, 255, 0.2)'
        }}>
          📈 Enhanced with 4-Persona Assessment Data (Assessment #{personaContext.assessmentId})
        </div>
      )}

      {/* Footer with company context */}
      {businessData && (
        <div style={{
          marginTop: '10px',
          padding: '10px',
          backgroundColor: 'var(--ra-deep-blue)',
          borderRadius: '6px',
          fontSize: '11px',
          color: 'var(--ra-text-secondary)',
          textAlign: 'center'
        }}>
          {businessData.companyProfile?.name} • {String(businessData.actualRevenue?.formatted || 'N/A')} Revenue
        </div>
      )}
    </div>
  );
};

export default BusinessImpactAnalysis;